import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const ProfileHeader = ({ user }) => {
  const navigate = useNavigate();

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'editor':
        return 'bg-secondary/10 text-secondary border-secondary/20';
      case 'reporter':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'viewer':
        return 'bg-accent/10 text-accent border-accent/20';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'editor':
        return 'Edit';
      case 'reporter':
        return 'PenTool';
      case 'viewer':
        return 'Eye';
      default:
        return 'User';
    }
  };

  return (
    <div className="bg-card rounded-xl shadow-elevated border border-border p-6 mb-6">
      <div className="flex flex-col sm:flex-row items-center sm:items-start space-y-4 sm:space-y-0 sm:space-x-6">
        {/* Profile Avatar */}
        <div className="relative">
          <div className="w-24 h-24 bg-gradient-primary rounded-full flex items-center justify-center shadow-soft">
            <Icon name="User" size={40} color="white" />
          </div>
          <div className="absolute -bottom-2 -right-2 w-8 h-8 bg-success rounded-full border-4 border-card flex items-center justify-center">
            <Icon name="CheckCircle" size={16} color="white" />
          </div>
        </div>

        {/* Profile Info */}
        <div className="flex-1 text-center sm:text-left">
          <h1 className="text-2xl font-heading font-bold text-foreground mb-2">
            {user?.name}
          </h1>
          <p className="text-muted-foreground mb-3">{user?.email}</p>
          
          <div className="flex flex-wrap items-center justify-center sm:justify-start gap-3">
            {/* Role Badge */}
            <div className={`inline-flex items-center space-x-2 px-3 py-1 rounded-full text-sm font-medium border ${getRoleColor(user?.role)}`}>
              <Icon name={getRoleIcon(user?.role)} size={14} />
              <span className="capitalize">{user?.role}</span>
            </div>

            {/* Join Date */}
            <div className="flex items-center space-x-2 text-sm text-muted-foreground">
              <Icon name="Calendar" size={14} />
              <span>Joined {user?.joinDate}</span>
            </div>

            {/* Verification Status */}
            <div className="flex items-center space-x-2 text-sm text-success">
              <Icon name="Shield" size={14} />
              <span>Verified Account</span>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4 text-center sm:text-left">
          <div>
            <div className="text-2xl font-bold text-foreground">{user?.usage?.newsGenerations?.used || 0}</div>
            <div className="text-xs text-muted-foreground">Articles</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">{user?.usage?.verifications?.used || 0}</div>
            <div className="text-xs text-muted-foreground">Verifications</div>
          </div>
          <div>
            <div className="text-2xl font-bold text-foreground">{user?.usage?.biasDetections?.used || 0}</div>
            <div className="text-xs text-muted-foreground">Bias Checks</div>
          </div>
        </div>
      </div>

      {/* Get Pro Section - Only show if user is not pro */}
      {!user?.pro && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="bg-gradient-to-r from-primary to-secondary rounded-lg p-4">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div className="flex items-center space-x-3 text-white">
                <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                  <Icon name="Zap" size={20} color="white" />
                </div>
                <div>
                  <h3 className="font-heading font-bold">Upgrade to Pro</h3>
                  <p className="text-white/90 text-sm">Unlock advanced features and unlimited access</p>
                </div>
              </div>
              <Button
                variant="default"
                className="bg-white text-primary hover:bg-white/90 font-semibold"
                iconName="Crown"
                iconPosition="left"
                onClick={() => navigate('/plans')}
              >
                Get Pro
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Pro Member Badge - Show if user is pro */}
      {user?.pro && (
        <div className="mt-6 pt-6 border-t border-border">
          <div className="bg-gradient-to-r from-yellow-500 to-yellow-600 rounded-lg p-4">
            <div className="flex items-center justify-center sm:justify-start gap-3 text-white">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <Icon name="Crown" size={20} color="white" />
              </div>
              <div>
                <h3 className="font-heading font-bold">Pro Member</h3>
                <p className="text-white/90 text-sm">
                  You have access to all premium features
                  {user?.planId && ` • ${user.planId.charAt(0).toUpperCase() + user.planId.slice(1)} Plan`}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProfileHeader;